import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { fmtEur } from "./ImFinancialViz";
import { TrendingUp, Sparkles, Quote, Trash2 } from "lucide-react";
import TeaserMarkdown from "./TeaserMarkdown";
import { toast } from "sonner";
import { t } from "../lib/i18n";

const fmtPct1 = (v) =>
  v === null || v === undefined || v === "" || isNaN(Number(v))
    ? "—"
    : `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

const fmtEurFull = (v) =>
  v === null || v === undefined || isNaN(Number(v)) ? "—" : `${Number(v).toLocaleString("es-ES")} €`;

// EBITDA = Margen bruto − Gastos operativos (si no hay margen bruto, se toma ingresos como base)
const calcEbitda = (y) => {
  const rev = Number(y.revenue) || 0;
  const opex = Number(y.opex) || 0;
  const gross = y.gross === "" || y.gross == null ? null : Number(y.gross);
  return (gross ?? rev) - opex;
};

const C_REV = "#1E3A5F";
const C_GROSS = "#E4B54B";
const C_EBITDA = "#2E6F80";

const ProformaChart = ({ years }) => {
  const data = years.map((y) => ({
    label: `${y.year}${y.projected ? " (P)" : ""}`,
    Ingresos: y.revenue,
    "Margen bruto": y.gross ?? null,
    EBITDA: y.ebitda,
  }));
  return (
    <div data-testid="proforma-chart">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#d7e0ea" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => fmtEur(v)} tick={{ fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} width={70} />
          <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ borderRadius: 0, border: "1px solid #d7e0ea", fontSize: 12 }} />
          <Bar dataKey={t("Ingresos")} fill={C_REV} />
          <Bar dataKey={t("Margen bruto")} fill={C_GROSS} />
          <Bar dataKey={t("EBITDA")} fill={C_EBITDA} />
        </BarChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap items-center justify-center gap-5 mt-2 text-xs">
        {[["Ingresos", C_REV], ["Margen bruto", C_GROSS], ["EBITDA", C_EBITDA]].map(([l, c]) => (
          <span key={l} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 inline-block" style={{ backgroundColor: c }} />
            <span className="text-muted-foreground">{l}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const ProformaTable = ({ years }) => (
  <div className="overflow-x-auto" data-testid="proforma-table">
    <table className="w-full text-sm">
      <thead>
        <tr className="hairline-b">
          <th className="text-left py-2.5 pr-4 font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{t("P&L Proforma")}</th>
          {years.map((y) => (
            <th key={y.year} className="text-right py-2.5 px-3 font-mono-label" style={{ fontSize: "0.6rem" }}>
              {y.year}
              <span className={y.projected ? "text-brand-orange" : "text-forest"}> {y.projected ? "(P)" : t("Real")}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[
          ["Ingresos", (y) => fmtEur(y.revenue), false],
          ["Margen bruto", (y) => (y.gross == null ? "—" : fmtEur(y.gross)), false],
          ["% Margen bruto", (y) => fmtPct1(y.gross_margin_pct), false],
          ["Gastos operativos", (y) => fmtEur(y.opex), false],
          ["EBITDA", (y) => fmtEur(y.ebitda), true],
          ["Margen EBITDA", (y) => fmtPct1(y.margin_pct), true],
        ].map(([label, fn, bold]) => (
          <tr key={label} className="hairline-b last:border-b-0">
            <td className={`py-2.5 pr-4 ${bold ? "font-semibold" : "text-muted-foreground"}`}>{label}</td>
            {years.map((y) => (
              <td key={y.year} className={`text-right py-2.5 px-3 tabular-nums ${bold ? "font-semibold" : ""}`}>{fn(y)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ProformaView = ({ proforma }) => (
  <div className="hairline hairline-t-0 bg-card p-6" data-testid="proforma-view">
    <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
      <h3 className="font-serif-display text-2xl">{t("P&L Proforma ·")}{" "}{proforma.horizon}{" "}{t("años vista")}</h3>
      <span className="font-mono-label text-muted-foreground">{t("Valores en € · (P) = Proyección")}{proforma.edited ? t("· revisada por el vendedor") : ""}</span>
    </div>
    <ProformaChart years={proforma.years} />
    <div className="mt-6">
      <ProformaTable years={proforma.years} />
    </div>
    <div className="mt-8 hairline border-l-4 border-l-[#E0712C] bg-bone" data-testid="proforma-justification">
      <div className="px-6 py-4 hairline-b flex items-center gap-3">
        <Quote className="w-4 h-4 text-[#C25F1E] flex-shrink-0" />
        <div>
          <h4 className="font-serif-display text-xl leading-tight">{t("Tesis de Crecimiento y Justificación")}</h4>
          <span className="font-mono-label text-muted-foreground">{t("En palabras del vendedor · texto íntegro")}</span>
        </div>
      </div>
      <div className="px-6 py-5 max-h-[520px] overflow-y-auto" data-testid="proforma-justification-body">
        {proforma.assumptions_text ? (
          <p className="text-base leading-relaxed whitespace-pre-line text-foreground">{proforma.assumptions_text}</p>
        ) : (
          <p className="text-sm italic text-muted-foreground">{t("El vendedor aún no ha aportado la justificación de estas proyecciones.")}</p>
        )}
      </div>
      {proforma.thesis_ai_text && (
        <div className="hairline-t bg-card" data-testid="proforma-thesis-ai">
          <div className="px-6 py-3 hairline-b flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            <span className="font-mono-label text-gold">{t("Ampliación analítica · redactada con IA a partir del texto del vendedor")}</span>
          </div>
          <article className="teaser-prose px-6 py-5 max-h-[640px] overflow-y-auto">
            <TeaserMarkdown text={proforma.thesis_ai_text} stripLeadingHeading />
          </article>
        </div>
      )}
    </div>
  </div>
);

export default function ProformaEditor({ company, onCompanyUpdate }) {
  const companyId = company.company_id;
  const proforma = company.im_proforma;
  const [assumptions, setAssumptions] = useState(proforma?.assumptions_text || "");
  const [horizon, setHorizon] = useState(proforma?.horizon || 4);
  const [years, setYears] = useState(proforma?.years || null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanding, setExpanding] = useState(false);

  const expandThesis = async () => {
    if (assumptions.trim().length < 30) {
      toast.error(t("Escribe primero tu tesis de crecimiento (mínimo 30 caracteres)"));
      return;
    }
    setExpanding(true);
    try {
      const r = await api.post(`/companies/${companyId}/im/proforma/thesis/expand`, { assumptions_text: assumptions.trim() });
      onCompanyUpdate(r.data.company);
      toast.success(t("Tesis ampliada con IA · tu texto original se mantiene íntegro"));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error ampliando la tesis");
    } finally {
      setExpanding(false);
    }
  };

  const removeExpansion = async () => {
    try {
      const r = await api.delete(`/companies/${companyId}/im/proforma/thesis/expand`);
      onCompanyUpdate(r.data.company);
      toast.success(t("Ampliación eliminada"));
    } catch {
      toast.error(t("Error eliminando la ampliación"));
    }
  };

  useEffect(() => {
    const p = company.im_proforma;
    if (p) {
      setYears(p.years);
      setAssumptions(p.assumptions_text || "");
      setHorizon(p.horizon || 4);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.im_proforma?.generated_at]);

  const generate = async () => {
    if (assumptions.trim().length < 30) {
      toast.error(t("Describe tu plan de crecimiento y márgenes (mínimo 30 caracteres)"));
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/companies/${companyId}/im/proforma/generate`, {
        assumptions_text: assumptions.trim(),
        horizon: Number(horizon),
      });
      onCompanyUpdate(r.data.company);
      setYears(r.data.company.im_proforma.years);
      toast.success(t("P&L proforma generado con IA · puedes editar cada celda"));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error generando la proyección");
    } finally {
      setGenerating(false);
    }
  };

  const setCell = (idx, key, value) => {
    setYears((prev) => prev.map((y, i) => (i === idx ? { ...y, [key]: value } : y)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/companies/${companyId}/im/proforma`, {
        assumptions_text: assumptions.trim() || null,
        years: years.map((y) => ({
          year: y.year,
          revenue: Number(y.revenue) || 0,
          opex: Number(y.opex) || 0,
          gross: y.gross === "" || y.gross == null ? null : Number(y.gross),
          projected: y.projected,
        })),
      });
      onCompanyUpdate(r.data.company);
      setYears(r.data.company.im_proforma.years);
      toast.success(t("Proforma guardada"));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hairline bg-card mb-8" data-testid="proforma-card">
      <div className="px-6 py-4 hairline-b bg-bone flex items-center gap-3">
        <TrendingUp className="w-4 h-4 text-gold" />
        <div>
          <span className="font-mono-label text-gold">{t("Proyecciones Financieras · P&L Proforma")}</span>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("Describe tu plan; la IA calcula la proyección base y tú editas cada celda. Tu texto se mostrará como justificación en el IM.")}
          </p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div>
          <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>
            {t("Tesis de Crecimiento y Justificación · plan y márgenes esperados")}
          </Label>
          <p className="text-xs text-[#C25F1E] mt-1 flex items-start gap-1">
            <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
            {t("Este texto se muestra ÍNTEGRO al inversor junto al gráfico, tal y como lo escribas. Si quieres, la IA lo amplía (nunca lo resume) en un bloque adicional.")}
          </p>
          <Textarea
            value={assumptions}
            onChange={(e) => setAssumptions(e.target.value)}
            rows={8}
            maxLength={8000}
            placeholder={t("Ej.: Crecimiento del 12% anual apoyado en la expansión a mid-market y 2 comerciales nuevos; el margen EBITDA mejora hacia el 50% por economías de escala y digitalización de la captación…")}
            className="rounded-none mt-1.5 text-sm w-full"
            data-testid="proforma-assumptions"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{assumptions.length}{" "}{t("caracteres")}</span>
            {years && (
              <div className="flex items-center gap-3">
                {proforma?.thesis_ai_text && (
                  <button type="button" onClick={removeExpansion} className="font-mono-label text-destructive inline-flex items-center gap-1 hover:opacity-80" data-testid="proforma-thesis-remove">
                    <Trash2 className="w-3 h-3" />{" "}{t("Quitar ampliación")}
                  </button>
                )}
                <Button
                  type="button"
                  onClick={expandThesis}
                  disabled={expanding}
                  variant="outline"
                  className="rounded-none h-9 px-4 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5"
                  data-testid="proforma-thesis-expand"
                >
                  <Sparkles className={`w-3.5 h-3.5 mr-2 ${expanding ? "animate-pulse" : ""}`} />
                  {expanding ? t("Ampliando con IA…") : proforma?.thesis_ai_text ? t("Volver a ampliar con IA") : t("Ampliar con IA (sin resumir)")}
                </Button>
              </div>
            )}
          </div>
          {proforma?.thesis_ai_text && (
            <div className="mt-3 hairline bg-bone" data-testid="proforma-thesis-ai-preview">
              <div className="px-4 py-2 hairline-b font-mono-label text-gold">{t("Ampliación IA · se mostrará debajo de tu texto")}</div>
              <article className="teaser-prose px-4 py-4 max-h-72 overflow-y-auto text-sm">
                <TeaserMarkdown text={proforma.thesis_ai_text} stripLeadingHeading />
              </article>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{t("Horizonte")}</Label>
            <select
              value={horizon}
              onChange={(e) => setHorizon(e.target.value)}
              className="hairline bg-card h-9 px-2 text-sm rounded-none"
              data-testid="proforma-horizon"
            >
              <option value={3}>{t("3 años")}</option>
              <option value={4}>{t("4 años")}</option>
              <option value={5}>{t("5 años")}</option>
            </select>
          </div>
          <Button
            onClick={generate}
            disabled={generating || !company.im_financials}
            className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-10 px-5"
            data-testid="proforma-generate-btn"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${generating ? "animate-pulse" : ""}`} />
            {generating ? t("Calculando con IA…") : years ? t("Regenerar proyección") : t("Generar proyección con IA")}
          </Button>
          {!company.im_financials && (
            <span className="font-mono-label text-destructive" style={{ fontSize: "0.6rem" }}>
              {t("Guarda antes los datos financieros (abajo)")}
            </span>
          )}
        </div>

        {years && (
          <>
            <div className="overflow-x-auto" data-testid="proforma-editor-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="hairline-b">
                    <th className="text-left py-2.5 pr-4 font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{t("Editable")}</th>
                    {years.map((y) => (
                      <th key={y.year} className="text-right py-2.5 px-2 font-mono-label" style={{ fontSize: "0.6rem" }}>
                        {y.year}
                        <span className={y.projected ? "text-brand-orange" : "text-forest"}> {y.projected ? "(P)" : t("Real")}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[["revenue", "Ingresos (€)"], ["gross", "Margen bruto (€)"], ["opex", "Gastos operativos sin coste de ventas (€)"]].map(([key, label]) => (
                    <tr key={key} className="hairline-b">
                      <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">{label}</td>
                      {years.map((y, i) => (
                        <td key={y.year} className="py-2 px-1">
                          <Input
                            type="number"
                            value={y[key] ?? ""}
                            onChange={(e) => setCell(i, key, e.target.value)}
                            className="rounded-none h-9 text-right text-sm tabular-nums min-w-[110px]"
                            data-testid={`proforma-cell-${key}-${y.year}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="hairline-b">
                    <td className="py-2.5 pr-4 font-semibold">{t("EBITDA (auto) = Margen bruto − Gastos")}</td>
                    {years.map((y) => (
                      <td key={y.year} className="text-right py-2.5 px-3 font-semibold tabular-nums" data-testid={`proforma-ebitda-${y.year}`}>
                        {fmtEurFull(calcEbitda(y))}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-4 font-semibold">{t("Margen (auto)")}</td>
                    {years.map((y) => {
                      const rev = Number(y.revenue) || 0;
                      const m = rev ? fmtPct1((calcEbitda(y) / rev) * 100) : "—";
                      return <td key={y.year} className="text-right py-2.5 px-3 font-semibold tabular-nums">{m}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-none text-white font-semibold h-10 px-6"
                style={{ backgroundColor: "#2E6F80" }}
                data-testid="proforma-save-btn"
              >
                {saving ? t("Guardando…") : t("Guardar proforma")}
              </Button>
            </div>
            {years && (
              <ProformaChart
                years={years.map((y) => {
                  const rev = Number(y.revenue) || 0;
                  const opex = Number(y.opex) || 0;
                  const gross = y.gross === "" || y.gross == null ? null : Number(y.gross);
                  return { ...y, revenue: rev, opex, gross, ebitda: calcEbitda(y) };
                })}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
